import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import styles from '../styles/AttendanceReportPage.module.css'; // Create this new CSS file

// Helper to get a date string in YYYY-MM-DD format
const getDateString = (date) => {
    const d = new Date(date);
    // Adjust for timezone to get the correct local date string
    const adjustedDate = new Date(d.getTime() - (d.getTimezoneOffset() * 60000));
    return adjustedDate.toISOString().split('T')[0];
};

const AttendanceReportPage = () => {
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState(getDateString(new Date()));

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch logs based on the selected date
    useEffect(() => {
        if (!selectedDate) return;

        const fetchLogsForDate = async () => {
            if (!isMounted.current) return;
            setLoading(true);
            setError('');

            try {
                // This query requires a single-field index on 'date'.
                // Firestore usually creates these automatically. If not, the console will provide a link.
                const logsQuery = query(
                    collection(db, 'attendance'),
                    where('date', '==', selectedDate),
                    orderBy('checkInTime', 'desc') // Show latest check-ins first
                );

                const querySnapshot = await getDocs(logsQuery);
                if (!isMounted.current) return;

                const logsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    checkInTime: doc.data().checkInTime?.toDate() // Convert Firestore Timestamp to JS Date
                }));
                setAttendanceLogs(logsData);

            } catch (err) {
                console.error(`Error fetching attendance for ${selectedDate}:`, err);
                if (isMounted.current) {
                    if (err.code === 'failed-precondition') {
                        setError("A database index is required for this query. Please check the browser console for a link to create it in Firebase.");
                    } else {
                        setError("Failed to fetch attendance logs for the selected date.");
                    }
                }
            } finally {
                if (isMounted.current) setLoading(false);
            }
        };

        fetchLogsForDate();
    }, [selectedDate]);

    const todayForDateInput = useMemo(() => getDateString(new Date()), []);

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>Daily Attendance Report</h1>
                <div className={styles.filterGroup}>
                    <label htmlFor="attendance-date-filter" className={styles.dateFilterLabel}>Report Date:</label>
                    <input
                        type="date"
                        id="attendance-date-filter"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className={styles.dateInput}
                        max={todayForDateInput}
                    />
                </div>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <div className={styles.tableContainer}>
                <table className={styles.reportTable}>
                    <thead>
                        <tr>
                            <th>Staff Member</th>
                            <th>Role</th>
                            <th>Check-in Time</th>
                            <th>Status</th>
                            <th>Distance From Office</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="5" className={styles.loadingIndicator}>Loading Report...</td>
                            </tr>
                        ) : attendanceLogs.length === 0 ? (
                            <tr>
                                <td colSpan="5" className={styles.noDataMessage}>No check-ins recorded for {selectedDate}.</td>
                            </tr>
                        ) : (
                            attendanceLogs.map(log => (
                                <tr key={log.id}>
                                    <td>{log.userName}</td>
                                    <td>{log.userRole || 'N/A'}</td>
                                    <td>{log.checkInTime ? log.checkInTime.toLocaleTimeString() : 'N/A'}</td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${styles.statusCheckedIn}`}>
                                            {log.status}
                                        </span>
                                    </td>
                                    <td>{log.distanceFromOffice !== undefined ? `${log.distanceFromOffice}m` : 'N/A'}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AttendanceReportPage;